import { Fragment, useMemo } from 'react';
import { StyleSheet } from 'react-native';

import Setting from 'modules/Settings/components/Setting';
import { RouteNames } from 'navigation/linkingConfig';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { SettingActions, SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';
import { HorizontalDivider, Text, withErrorBoundary } from 'components';

import { Section, SectionContainer } from './components/Section';
import { SettingError } from './components/SettingError';
import type { TextStyle } from 'react-native';
import type {
  ActionButtonsInterfaceSettingKey,
  ItemListInterfaceSettingKey,
  ListViewPreviewInterfaceSettingKey,
  OrderHeaderInterfaceSettingKey,
} from 'store/Settings/types';

const ItemListSection = () => {
  const dispatch = useAppDispatch();

  const logEvent = useLogEventCallback();

  const currentSettings = useAppSelector(
    SettingSelectors.selectItemListInterfaceSettings,
  );

  const interfaceSettingsFeatureFlag = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const settingKeys: ItemListInterfaceSettingKey[] = useMemo(() => {
    const data: (ItemListInterfaceSettingKey | false)[] = [
      interfaceSettingsFeatureFlag.item_list.item_pricing &&
        'shouldDisplayItemPricing',
    ];
    return data.filter((d): d is Exclude<typeof d, false> => d !== false);
  }, [interfaceSettingsFeatureFlag.item_list.item_pricing]);

  const toggle = (key: ItemListInterfaceSettingKey) => () => {
    const payload = {
      [key]: !currentSettings[key],
    };

    dispatch(SettingActions.setItemListInterface(payload));
    logEvent(AnalyticsEvents.interfaceSettingsUpdated(payload));
  };

  return (
    <SectionContainer titleKey="settings.interface.itemList.title">
      {settingKeys.length > 0 ? (
        settingKeys.map((key, index) => {
          return (
            <Fragment key={key}>
              <Section
                titleKey={`settings.interface.itemList.${key}.title`}
                subtitleKey={`settings.interface.itemList.${key}.subtitle`}
                isChecked={currentSettings[key]}
                onPressCheckbox={toggle(key)}
              />
              {index !== settingKeys.length - 1 && (
                <HorizontalDivider style={styles.divider} />
              )}
            </Fragment>
          );
        })
      ) : (
        <NoContentText />
      )}
    </SectionContainer>
  );
};

const NoContentText = () => {
  return (
    <Text
      style={
        {
          marginVertical: 8,
          textAlign: 'center',
        } satisfies TextStyle
      }
      id="settings.noSettingsAvailable"
    ></Text>
  );
};

const OrderHeaderSection = () => {
  const dispatch = useAppDispatch();

  const logEvent = useLogEventCallback();

  const currentSettings = useAppSelector(
    SettingSelectors.selectOrderHeaderInterfaceSettings,
  );
  const featureFlagInterfaceSettings = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const settingKeys: OrderHeaderInterfaceSettingKey[] = useMemo(() => {
    const data: (OrderHeaderInterfaceSettingKey | false)[] = [
      featureFlagInterfaceSettings.order_header.customer_name &&
        'shouldDisplayCustomerName',
      featureFlagInterfaceSettings.order_header.customer_address &&
        'shouldDisplayCustomerAddress',
      featureFlagInterfaceSettings.order_header.customer_phone_number &&
        'shouldDisplayCustomerPhoneNumber',
      featureFlagInterfaceSettings.order_header.kds_custom_fields &&
        'shouldDisplayCustomFields',
      featureFlagInterfaceSettings.order_header.order_channel &&
        'shouldDisplayOrderChannel',
      featureFlagInterfaceSettings.order_header.driver_details &&
        'shouldDisplayDriverDetails',
      featureFlagInterfaceSettings.order_header.total_order_value &&
        'shouldDisplayTotalOrderValue',
      featureFlagInterfaceSettings.order_header.number_of_items &&
        'shouldDisplayNumberOfItems',
      featureFlagInterfaceSettings.order_header.order_type &&
        'shouldDisplayOrderType',
    ];

    return data.filter((d): d is Exclude<typeof d, false> => d !== false);
  }, [
    featureFlagInterfaceSettings.order_header.customer_address,
    featureFlagInterfaceSettings.order_header.customer_name,
    featureFlagInterfaceSettings.order_header.customer_phone_number,
    featureFlagInterfaceSettings.order_header.driver_details,
    featureFlagInterfaceSettings.order_header.kds_custom_fields,
    featureFlagInterfaceSettings.order_header.number_of_items,
    featureFlagInterfaceSettings.order_header.order_channel,
    featureFlagInterfaceSettings.order_header.order_type,
    featureFlagInterfaceSettings.order_header.total_order_value,
  ]);

  const toggle = (key: OrderHeaderInterfaceSettingKey) => () => {
    const payload = {
      [key]: !currentSettings[key],
    };

    dispatch(SettingActions.setOrderHeaderInterface(payload));
    logEvent(AnalyticsEvents.orderHeaderSettingsUpdated(payload));
  };

  return (
    <SectionContainer titleKey="settings.interface.orderHeader.title">
      {settingKeys.length > 0 ? (
        settingKeys.map((key, index) => {
          return (
            <Fragment key={key}>
              <Section
                titleKey={`settings.interface.orderHeader.${key}.title`}
                subtitleKey={`settings.interface.orderHeader.${key}.subtitle`}
                isChecked={currentSettings[key]}
                onPressCheckbox={toggle(key)}
              />
              {index !== settingKeys.length - 1 && (
                <HorizontalDivider style={styles.divider} />
              )}
            </Fragment>
          );
        })
      ) : (
        <NoContentText />
      )}
    </SectionContainer>
  );
};

const ListViewPreviewSection = () => {
  const dispatch = useAppDispatch();

  const logEvent = useLogEventCallback();

  const currentSettings = useAppSelector(
    SettingSelectors.selectListViewPreviewInterfaceSettings,
  );

  const featureFlagInterfaceSettings = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const settingKeys: ListViewPreviewInterfaceSettingKey[] = useMemo(() => {
    const data: (ListViewPreviewInterfaceSettingKey | false)[] = [
      featureFlagInterfaceSettings.list_view_preview.customer_name &&
        'shouldDisplayCustomerName',
      featureFlagInterfaceSettings.list_view_preview.customer_address &&
        'shouldDisplayCustomerAddress',
      featureFlagInterfaceSettings.list_view_preview.customer_phone_number &&
        'shouldDisplayCustomerPhoneNumber',
      featureFlagInterfaceSettings.list_view_preview.total_order_value &&
        'shouldDisplayTotalOrderValue',
      featureFlagInterfaceSettings.list_view_preview.number_of_items &&
        'shouldDisplayNumberOfItems',
    ];

    return data.filter((d): d is Exclude<typeof d, false> => d !== false);
  }, [
    featureFlagInterfaceSettings.list_view_preview.customer_address,
    featureFlagInterfaceSettings.list_view_preview.customer_name,
    featureFlagInterfaceSettings.list_view_preview.customer_phone_number,
    featureFlagInterfaceSettings.list_view_preview.number_of_items,
    featureFlagInterfaceSettings.list_view_preview.total_order_value,
  ]);

  const toggle = (key: ListViewPreviewInterfaceSettingKey) => () => {
    const payload = {
      [key]: !currentSettings[key],
    };

    dispatch(SettingActions.setListViewPreviewInterface(payload));
    logEvent(AnalyticsEvents.listViewPreviewSettingsUpdated(payload));
  };

  return (
    <SectionContainer titleKey="settings.interface.listViewPreview.title">
      {settingKeys.length > 0 ? (
        settingKeys.map((key, index) => {
          return (
            <Fragment key={key}>
              <Section
                titleKey={`settings.interface.listViewPreview.${key}.title`}
                subtitleKey={`settings.interface.listViewPreview.${key}.subtitle`}
                isChecked={currentSettings[key]}
                onPressCheckbox={toggle(key)}
              />
              {index !== settingKeys.length - 1 && (
                <HorizontalDivider style={styles.divider} />
              )}
            </Fragment>
          );
        })
      ) : (
        <NoContentText />
      )}
    </SectionContainer>
  );
};
const ActionButtonsSection = () => {
  const dispatch = useAppDispatch();

  const logEvent = useLogEventCallback();

  const currentSettings = useAppSelector(
    SettingSelectors.selectActionButtonsInterfaceSettings,
  );

  const settingKeys: ActionButtonsInterfaceSettingKey[] = useMemo(() => {
    const data: ActionButtonsInterfaceSettingKey[] = [
      'shouldDisplayAllActionButton',
      'shouldDisplayIndividualActionButton',
    ];

    return data.filter(Boolean);
  }, []);

  const toggle = (key: ActionButtonsInterfaceSettingKey) => () => {
    const payload = {
      [key]: !currentSettings[key],
    };

    dispatch(SettingActions.setActionButtonsInterface(payload));
    logEvent(AnalyticsEvents.actionButtonSettingsUpdated(payload));
  };

  return (
    <SectionContainer titleKey="settings.interface.actionButtons.title">
      {settingKeys.length > 0 ? (
        settingKeys.map((key, index) => {
          return (
            <Fragment key={key}>
              <Section
                titleKey={`settings.interface.actionButtons.${key}.title`}
                subtitleKey={`settings.interface.actionButtons.${key}.subtitle`}
                isChecked={currentSettings[key]}
                onPressCheckbox={toggle(key)}
              />
              {index !== settingKeys.length - 1 && (
                <HorizontalDivider style={styles.divider} />
              )}
            </Fragment>
          );
        })
      ) : (
        <NoContentText />
      )}
    </SectionContainer>
  );
};

const Interface = () => {
  return (
    <Setting
      titleKey="settings.interface.title"
      subTitleKey="settings.interface.subtitle"
      iconName="monitor-edit"
    >
      {/* <ItemDetailsSection />
      <OrderListSection />
      <OrderDetailsSection /> */}

      <OrderHeaderSection />
      <ItemListSection />
      <ListViewPreviewSection />
      <ActionButtonsSection />
    </Setting>
  );
};

const styles = StyleSheet.create({
  divider: {
    marginVertical: 20,
  },
});

export default withErrorBoundary(Interface, {
  screen: RouteNames.Home.Settings.Sounds,
  key: 'settings-interface',
  fallback: <SettingError />,
});
